import React from 'react';
import { AlertCircle } from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { QUOTA_EXCEEDED_MESSAGE } from '../../../services/apiClient';
import { INDIVIDUAL_QUOTA_EXCEEDED_TEXT } from '../../../utils/productAccess';
import { personaAssetUrl } from '../services/personaService';

export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const statusLabel = (status) => {
  const labels = {
    draft: '작성 중',
    pending: '대기',
    running: '실행 중',
    completed: '완료',
    failed: '실패',
  };
  return labels[status] || status || '-';
};

export const getPersonaMeta = (persona) => {
  const profile = persona?.profile || {};
  return [
    persona?.gender || profile.gender,
    persona?.age ? `${persona.age}세` : profile.age ? `${profile.age}세` : '',
    persona?.title || profile.job || profile.occupation,
  ].filter(Boolean).join(' · ');
};

export const getPersonaInitial = (name) => (String(name || 'P').trim()[0] || 'P').toUpperCase();

export const PERSONA_TAG_MAX_LENGTH = 20;

export function normalizePersonaTag(value) {
  const tag = String(value || '').trim();
  return tag ? tag.slice(0, PERSONA_TAG_MAX_LENGTH) : '';
}

export function formatPersonaTagLabel(value) {
  return normalizePersonaTag(value);
}

export function PersonaTagChip({ tag, className = '' }) {
  const normalized = normalizePersonaTag(tag);
  if (!normalized) return null;
  return (
    <span className={['persona-tag-chip', className].filter(Boolean).join(' ')} title={normalized}>
      {formatPersonaTagLabel(normalized)}
    </span>
  );
}

export function PersonaShell({ eyebrow, title, description, action, children, wide = false }) {
  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      <div className={`persona-page ${wide ? 'persona-page--wide' : ''}`}>
        <header className="persona-page__header">
          <div>
            {eyebrow ? <p className="persona-page__eyebrow">{eyebrow}</p> : null}
            <h1>{title}</h1>
            {description ? <p>{description}</p> : null}
          </div>
          {action ? <div className="persona-page__header-action">{action}</div> : null}
        </header>
        {children}
      </div>
    </B2BLayout>
  );
}

export function PersonaState({ type = 'empty', title, message, action }) {
  return (
    <div className={`persona-state persona-state--${type}`}>
      <h2>{title}</h2>
      {message ? <p>{message}</p> : null}
      {action ? <div className="persona-state__action">{action}</div> : null}
    </div>
  );
}

export function PersonaAvatar({ persona, size = 'md' }) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const imageUrl = personaAssetUrl(persona?.image_url || persona?.imageUrl);

  React.useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <div className={`persona-avatar persona-avatar--${size}`}>
      {imageUrl && !imageFailed ? (
        <img
          src={imageUrl}
          alt={persona?.name || 'Persona'}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span>{getPersonaInitial(persona?.name)}</span>
      )}
    </div>
  );
}

export function PersonaCard({ persona, onClick }) {
  const telecom = persona?.telecom_profile || {};
  const tags = [
    telecom.membership_usage_tendency,
    telecom.digital_literacy,
    persona?.source_type,
  ].filter(Boolean).slice(0, 3);

  return (
    <button type="button" className="persona-card" onClick={onClick}>
      <PersonaAvatar persona={persona} />
      <div className="persona-card__body">
        <h3>{persona.name}</h3>
        <p>{getPersonaMeta(persona) || persona.personality || '프로필 정보 없음'}</p>
        {normalizePersonaTag(persona?.tag) || tags.length > 0 ? (
          <div className="persona-tags">
            <PersonaTagChip tag={persona?.tag} />
            {tags.map((tag) => <span key={tag}>{tag}</span>)}
          </div>
        ) : null}
      </div>
    </button>
  );
}

export function ErrorBanner({ message }) {
  if (
    !message
    || message === QUOTA_EXCEEDED_MESSAGE
    || message === INDIVIDUAL_QUOTA_EXCEEDED_TEXT
  ) return null;
  return <div className="persona-error-banner">{message}</div>;
}

export const formatTeamTokenCount = (value) => {
  if (value === null || value === undefined) return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return numeric.toLocaleString('ko-KR');
};

export const formatTeamTokenDisplay = (value) => {
  if (value === null || value === undefined) return '-개';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-개';
  const clamped = Math.max(0, numeric);
  return `${clamped.toLocaleString('ko-KR')}개`;
};

export function TokenQuotaCtaHint({
  remainingTokens = null,
  loading = false,
  warningMessage = '',
}) {
  const showBalance = loading || remainingTokens !== null;
  const showWarning = Boolean(warningMessage);
  const isDepleted = !loading && remainingTokens !== null && Number(remainingTokens) <= 0;
  if (!showBalance && !showWarning) return null;

  return (
    <div className={`persona-quota-cta-panel${showWarning ? ' persona-quota-cta-panel--warning' : ''}`}>
      {showBalance ? (
        <p className="persona-quota-cta-balance">
          현재 잔여 토큰:{' '}
          <strong className={isDepleted ? 'is-depleted' : ''}>
            {loading ? '...' : formatTeamTokenDisplay(remainingTokens)}
          </strong>
        </p>
      ) : null}
      {showWarning ? (
        <p className="persona-quota-cta-hint" role="alert">
          <AlertCircle size={16} strokeWidth={1.8} aria-hidden="true" />
          <span>{warningMessage}</span>
        </p>
      ) : null}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="persona-field">
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export function ResultTable({ results, kind = 'ui' }) {
  if (!results?.length) {
    return <PersonaState title="결과가 없습니다" message="테스트를 실행하면 persona별 결과가 여기에 표시됩니다." />;
  }

  return (
    <div className="persona-table-wrap">
      <table className="persona-table">
        <thead>
          <tr>
            <th>Persona</th>
            <th>Status</th>
            <th>Score</th>
            <th>Summary</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result) => {
            const snapshot = result.persona_snapshot || {};
            const score = kind === 'ab'
              ? result.scores?.preference ?? result.scores?.overall
              : result.scores?.overall ?? result.scores?.score;
            const summary = result.summary || result.feedback?.summary || result.feedback?.reason || result.error_message;
            return (
              <tr key={result.id}>
                <td>{snapshot.name || (result.persona_id ? `#${result.persona_id}` : '전체')}</td>
                <td><span className={`persona-status persona-status--${result.status || 'completed'}`}>{statusLabel(result.status || 'completed')}</span></td>
                <td>{score ?? '-'}</td>
                <td>{summary || '-'}</td>
                <td>{formatDateTime(result.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
